import { motion } from "framer-motion";
import { Compass, Home, Building2, ArrowRight, Check } from "lucide-react";
import Seo from "../components/Seo";
import WaitlistForm from "../components/WaitlistForm";
import { CONTACT_EMAIL } from "../data/products";

const SERVICES = [
  {
    slug: "service-coaching",
    icon: Compass,
    name: "Low-Tox Coaching",
    tagline: "Personalized guidance to prioritize what matters and ignore the noise.",
    cta: "Request This Service",
    points: [
      "1:1 sessions focused on your home, products, and routines",
      "A prioritized action plan based on cumulative-load impact",
      "Decision frameworks you keep and reuse, no dependency",
      "Support for sensitive life stages (pregnancy, infants, chronic conditions)",
    ],
  },
  {
    slug: "service-systems-setup",
    icon: Home,
    name: "Household Systems Setup",
    tagline: "Done-with-you implementation of durable, low-tox home systems.",
    cta: "Request This Service",
    points: [
      "Room-by-room exposure audit across the four domains",
      "Material, air, water, and product recommendations with sourcing",
      "Implementation sequencing matched to budget and effort",
      "A maintainable system that lowers ongoing cognitive load",
    ],
  },
  {
    slug: "service-operational-systems",
    icon: Building2,
    name: "Operational Systems Design",
    tagline: "Engineering-based redesign of organizational systems.",
    cta: "Request Consultation",
    points: [
      "Workflow engineering and process optimization",
      "Decision architecture and governance frameworks",
      "Cognitive load allocation across teams",
      "SOP development and documentation systems",
      "Practical AI workflow implementation",
      "Capacity planning for sustainable performance",
    ],
  },
];

export default function ServicesPage() {
  return (
    <div data-testid="services-page">
      <Seo title="Services, Coaching & Household Systems Setup" path="/services"
        description="Work directly with My Eco Engineer. Low-tox coaching and done-with-you household systems setup, built on engineering-based frameworks." />

      <section className="grid-bg grain border-b border-border">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24">
          <p className="mono-label">Services · work with us</p>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl tracking-tight max-w-3xl leading-[1.05]">
            Want a faster, tailored path? <span className="text-primary">Let's build it together.</span>
          </h1>
          <p className="mt-7 max-w-2xl text-base md:text-lg text-foreground/65 leading-relaxed">
            For those who'd rather not navigate alone, we offer direct guidance and hands-on implementation, applying
            the same engineering rigor behind our Greenpapers to your home, your household, and your organization.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-border border border-border">
          {SERVICES.map((s, i) => (
            <motion.div key={s.slug} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="bg-surface p-8 lg:p-10 flex flex-col">
              <span className="flex h-12 w-12 items-center justify-center border border-primary/40 text-primary"><s.icon className="h-6 w-6" /></span>
              <h2 className="mt-6 font-display text-2xl font-medium">{s.name}</h2>
              <p className="mt-2 text-foreground/60">{s.tagline}</p>
              <ul className="mt-6 space-y-2.5 flex-1">
                {s.points.map((p, j) => (
                  <li key={j} className="flex gap-3 text-sm text-foreground/75"><Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />{p}</li>
                ))}
              </ul>
              <div className="mt-8 border-t border-border pt-6">
                <p className="mono-label mb-3">{s.cta}</p>
                <WaitlistForm product={{ slug: s.slug, name: s.name }} ctaLabel={s.cta} />
              </div>
            </motion.div>
          ))}
        </div>
        <p className="mt-8 text-sm text-muted-foreground">
          Prefer email? Reach us at <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary hover:underline">{CONTACT_EMAIL}</a>.
          Services are offered on a limited basis, joining the list reserves your place and we'll follow up with availability and pricing.
        </p>
      </section>
    </div>
  );
}
